import React, { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';

import { serverContext } from '../App'; // adjust path if needed
import { SensorPlayer, SensorPlayerToolbar } from 'stserver-frontend-comp';
import VideoCoverageChart from './VideoCoverageChart';
import { getSafeLiveMode, isLiveDvrReady } from './liveDvr';

// Extra vertical pixels taken by the toolbar sitting above the <video>
const TOOLBAR_H = 24;
const DEFAULT_AR = 16 / 9; // fallback before first real frame arrives

/**
 * LivePlayer – width‑driven player tile.
 * ---------------------------------------------------------------
 * • Parent supplies **width** only; component keeps its own aspect‑ratio.
 * • Aspect‑ratio is updated from SensorPlayer's onDimensionsChange but we
 *   subtract TOOLBAR_H so we don't add 24 px on every loop (the root cause
 *   of the “ever‑growing height” bug).
 */
function LivePlayer({
  /* layout */
  playerWidth,
  /* identity */
  sensor,
  index,
  playerRef,
  wrapperClass,
  masterSensorName,
  liveMode,
  classes,
  videoQueryChart,
  /* callbacks */
  onPlayerClick,
  onLiveControlModeChange,
  onLiveModeControlError,
  onKlvDataReceive,
  onResolutionDetect,
  onDimensionsChange, // optional – parent gets raw video dims (without toolbar)
  onVideoCoverageChartClick,
}) {
  const { server } = useContext(serverContext);
  const sensorKey = `${sensor.parentPlatformName}/${sensor.name}`;
  const dvrReady = isLiveDvrReady(sensor);
  const dvrAvailabilityKey = dvrReady ? 'dvr-ready' : 'dvr-warmup';
  const effectiveLiveMode = getSafeLiveMode(liveMode, sensor);

  // ─────────────── internal size state ────────────────
  const [ar, setAr] = useState(DEFAULT_AR); // aspect‑ratio (w / h)
  const playerHeight = Math.round(playerWidth / ar);

  /**
   * Guarded update of the aspect‑ratio.
   * We subtract the toolbar before calculating AR to avoid the additive
   * +24 px error that led to the infinite growth.
   */
  const handleDimensionsChange = useCallback(
    (w, h) => {
      // h reported by SensorPlayer may include the toolbar; remove it first.
      const videoH = Math.max(h - TOOLBAR_H, 1);
      const newAr = w / videoH;

      // Ignore jitter < 1 % to prevent ping‑pong.
      if (Math.abs(newAr - ar) / newAr < 0.01) return;

      setAr(newAr);
      onDimensionsChange?.(w, videoH); // bubble up pure video size
    },
    [ar, onDimensionsChange]
  );

  // ─────────────────────── render ──────────────────────
  return (
    <Grid item onClick={evt => onPlayerClick(evt, sensorKey, index)}>
      <Paper elevation={2} className={wrapperClass}>
        {/* toolbar + video */}
        <Box
          width={playerWidth}
          height={playerHeight + TOOLBAR_H}
          display="inline-block"
        >
          <SensorPlayerToolbar
            key={`${sensorKey}-${dvrAvailabilityKey}`}
            serverUrl={server.serverHost}
            username={server.username}
            password={server.password}
            token={server.token}
            useLiveLowLatency={server.useLiveLowLatency}
            useWebRtcVideo={server.useWebRtcVideo}
            useLiveRtspHls={server.useLiveRtspHls}
            live
            master={sensorKey === masterSensorName}
            allowLiveHighQualityModeWithoutQuery={dvrReady}
            missionIdOrName={sensor.m}
            sensorIdOrName={sensor.s}
            platformName={sensor.parentPlatformName}
            sensorName={sensor.name}
            mode={effectiveLiveMode}
            onLiveControlModeChange={onLiveControlModeChange}
            onError={onLiveModeControlError}
          />

          <SensorPlayer
            ref={playerRef}
          //  width={playerWidth}
          //  height={playerHeight}
            mode={effectiveLiveMode}
            webRtcService={server.webRtcService}
            serverUrl={server.serverHost}
            webRtcServerUrl={server.webRtcServerUrl}
            iceServers={server.iceServers}
            token={server.token}
            clientId={server.clientId}
            clientIp={server.clientIp}
            useReverseProxy={server.useReverseProxy}
            wsVideoStreamPort={sensor.config?.wsVideoStreamPort}
            videoDir="videos"
            platformName={sensor.parentPlatformName}
            sensorName={sensor.name}
            missionIdOrName={sensor.mission}
            sensorIdOrName={sensor.name}
            showControls
            username={server.username}
            password={server.password}
            browserInfo={server.browserInfo}
            onDataReceive={onKlvDataReceive}
            onResolutionDetect={onResolutionDetect}
            onDimensionsChange={handleDimensionsChange}
          />
        </Box>

        {/* optional coverage chart */}
        {videoQueryChart && videoQueryChart.id === `${sensor.m}/${sensor.s}` && (
          <Box className={classes.videoCoverageChart}>
            <VideoCoverageChart
              data={videoQueryChart.data}
              onClick={onVideoCoverageChartClick}
            />
          </Box>
        )}
      </Paper>
    </Grid>
  );
}

LivePlayer.propTypes = {
  playerWidth: PropTypes.number.isRequired,
  sensor: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  playerRef: PropTypes.object.isRequired,
  wrapperClass: PropTypes.string.isRequired,
  masterSensorName: PropTypes.string,
  liveMode: PropTypes.string,
  classes: PropTypes.object.isRequired,
  videoQueryChart: PropTypes.object,
  onPlayerClick: PropTypes.func.isRequired,
  onLiveControlModeChange: PropTypes.func.isRequired,
  onLiveModeControlError: PropTypes.func.isRequired,
  onKlvDataReceive: PropTypes.func.isRequired,
  onResolutionDetect: PropTypes.func.isRequired,
  onDimensionsChange: PropTypes.func,
  onVideoCoverageChartClick: PropTypes.func.isRequired,
};

export default LivePlayer;
